import { Link } from "react-router-dom";
import {
    Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { KPI_METRICS, selectVacancies } from "../../executive/boardLayout";
import { menuItemsForRole, isNavGroup } from "../../config/navConfig";

import { normalizeDashboardTask } from "../../utils/dashboardTasks";

/** Окна планшета руководителя. [Claude] 2026-11-03, правка №41. */

const fmt = (v, digits = 0) => (v === null || v === undefined || Number.isNaN(v) ? "—" : Number(v).toLocaleString("ru-RU", { maximumFractionDigits: digits }));
const shortDate = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" }) : "");
const weekday = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("ru-RU", { weekday: "short" }) : "");

const LEVEL = {
    red: "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    info: "bg-sky-50 border-sky-200 text-sky-800",
};
const DOT = { red: "bg-red-500", amber: "bg-amber-400", green: "bg-emerald-500" };
const LINKS = {
    vacancies: "/vacancies", candidates: "/candidates", calendar: "/calendar", requests: "/requests",
    adaptation: "/tests/adaptation", tests: "/tests/results",
};
const STAGE_COLS = [
    ["new", "Отклики"], ["cold", "Холод."], ["test", "Тест"], ["interview", "Собес"], ["test_day", "Тест-день"], ["offer", "Оффер"], ["docs", "Оформл."],
];
const FLAG_SHORT = { overdue: "срок", no_candidates: "нет кандидатов", stale: "нет движения", long_open: "долго открыта" };
const RISK_LABEL = { low: "Низкий", medium: "Средний", high: "Высокий", critical: "Критический", unknown: "Не рассчитан", uncalculated: "Не рассчитан" };
const DELIVERY_LABEL = { sent: "Отправлен", pending: "В очереди", retry: "Повтор", failed: "Ошибка", completed: "Пройден", cancelled: "Отменён", filtered: "Не отправлен (фильтр)" };
const OUTCOME_LABEL = { stable: "Стабильно", critical: "Критично", "без итога": "Без итога" };
const KIND_LABEL = { week_1: "1 неделя", month_1: "1 месяц", month_2: "2 месяца", control_2m: "Контроль 2 мес.", extra: "Доп. срез", custom: "Свой этап" };
const RISK_TONE = { low: "bg-emerald-500", medium: "bg-amber-400", high: "bg-red-500", critical: "bg-red-700" };

export function BlockError({ block }) {
    if (!block || !block.error) return null;
    return <div className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">Не удалось посчитать: {block.error}</div>;
}

function Bars({ rows, valueKey = "count", labelKey = "label", total, tone = "bg-[#e0bb48]" }) {
    const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
    return (
        <ul className="space-y-1">
            {rows.map((r) => (
                <li key={r[labelKey]} className="grid grid-cols-[minmax(110px,40%)_1fr_auto] items-center gap-2 text-sm">
                    <span className="truncate text-slate-600" title={r[labelKey]}>{r[labelKey]}</span>
                    <span className="h-3 bg-slate-100 rounded overflow-hidden">
                        <span className={`block h-full ${tone}`} style={{ width: `${(100 * (r[valueKey] || 0)) / max}%` }} />
                    </span>
                    <span className="tabular-nums font-medium text-slate-800 w-14 text-right">
                        {fmt(r[valueKey])}
                        {total ? <span className="text-slate-400 font-normal"> · {fmt((100 * (r[valueKey] || 0)) / total)}%</span> : null}
                    </span>
                </li>
            ))}
        </ul>
    );
}

function Stat({ label, value, hint, tone = "text-slate-900" }) {
    return (
        <div className="rounded-lg bg-slate-50 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 leading-tight">{label}</div>
            <div className={`text-2xl font-bold tabular-nums ${tone}`}>{value}</div>
            {hint ? <div className="text-xs text-slate-400">{hint}</div> : null}
        </div>
    );
}

export function AlertsWidget({ data, options }) {
    const alerts = (data?.alerts || []).slice(0, Number(options.limit) || 8);
    if (!alerts.length) return <div className="text-sm text-emerald-700">Всё спокойно: просрочек и зависших вакансий нет.</div>;
    return (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {alerts.map((a) => (
                <Link key={a.title} to={LINKS[a.link] || "/dashboard"} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm hover:shadow-sm ${LEVEL[a.level] || LEVEL.info}`}>
                    <span className="leading-snug">{a.title}</span>
                    <span className="text-xl font-bold tabular-nums">{fmt(a.count)}</span>
                </Link>
            ))}
        </div>
    );
}

export function KpiWidget({ data, options }) {
    const kpi = data?.kpi || {};
    const forbidden = new Set(data?.meta?.forbidden || []);
    const chosen = (Array.isArray(options.metrics) && options.metrics.length ? options.metrics : KPI_METRICS.map((m) => m.key))
        .filter((key) => !forbidden.has(KPI_METRICS.find((m) => m.key === key)?.block));
    const tone = { adaptation_overdue: kpi.adaptation_overdue ? "text-red-600" : undefined, lost: "text-slate-700" };
    return (
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-4 xl:grid-cols-8">
            {KPI_METRICS.filter((m) => chosen.includes(m.key)).map((m) => (
                <Stat key={m.key} label={m.label} value={fmt(kpi[m.key], 1)} tone={tone[m.key]} />
            ))}
        </div>
    );
}

export function VacanciesWidget({ data, options }) {
    const pipe = data?.pipeline || {};
    const rows = selectVacancies((pipe.vacancies || []).filter((v) => !options.department || v.department === options.department), options);
    const health = pipe.health || {};
    const showStages = options.stages !== false;
    return (
        <div className="space-y-2">
            <BlockError block={pipe} />
            <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                <span>Открыто: <b className="text-slate-900">{fmt(pipe.open_vacancies)}</b></span>
                <span className="flex items-center gap-1"><i className={`w-2.5 h-2.5 rounded-full ${DOT.red}`} />{fmt(health.red)}</span>
                <span className="flex items-center gap-1"><i className={`w-2.5 h-2.5 rounded-full ${DOT.amber}`} />{fmt(health.amber)}</span>
                <span className="flex items-center gap-1"><i className={`w-2.5 h-2.5 rounded-full ${DOT.green}`} />{fmt(health.green)}</span>
                <span className="text-slate-400">показано {rows.length}</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                            <th className="py-1.5 pr-2 w-4" />
                            <th className="py-1.5 pr-2">Вакансия</th>
                            <th className="py-1.5 pr-2">Отдел</th>
                            <th className="py-1.5 pr-2 text-right">Дней</th>
                            <th className="py-1.5 pr-2 text-right">В работе</th>
                            <th className="py-1.5 pr-2 text-right" title="Вышли на работу за период">Вышли</th>
                            {showStages && STAGE_COLS.map(([k, l]) => <th key={k} className="py-1.5 px-1 text-right">{l}</th>)}
                            <th className="py-1.5 px-2 text-right">Движение</th>
                            <th className="py-1.5 px-2">Срок</th>
                            <th className="py-1.5 pl-2">Сигналы</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((v) => (
                            <tr key={v.id} className="border-b border-slate-50 hover:bg-slate-50">
                                <td className="py-1 pr-2"><i className={`block w-2.5 h-2.5 rounded-full ${DOT[v.severity]}`} /></td>
                                <td className="py-1 pr-2 font-medium text-slate-900">
                                    <Link to={`/vacancies/${v.id}/tests`} className="hover:text-[#b8952b]">{v.name}</Link>
                                    {v.on_hh ? <span className="ml-1 text-[10px] text-slate-400">hh</span> : null}
                                </td>
                                <td className="py-1 pr-2 text-slate-500 whitespace-nowrap">{v.department || "—"}</td>
                                <td className="py-1 pr-2 text-right tabular-nums">{fmt(v.days_open)}</td>
                                <td className="py-1 pr-2 text-right tabular-nums font-semibold">{fmt(v.active)}</td>
                                <td className={`py-1 pr-2 text-right tabular-nums ${v.hired_in_period ? "text-emerald-700 font-semibold" : "text-slate-300"}`}>{v.hired_in_period || 0}</td>
                                {showStages && STAGE_COLS.map(([k]) => (
                                    <td key={k} className={`py-1 px-1 text-right tabular-nums ${v.stages?.[k] ? "text-slate-800" : "text-slate-300"}`}>{v.stages?.[k] || 0}</td>
                                ))}
                                <td className="py-1 px-2 text-right tabular-nums text-slate-500">{v.last_move_days === null || v.last_move_days === undefined ? "—" : `${v.last_move_days} дн.`}</td>
                                <td className="py-1 px-2 whitespace-nowrap text-slate-500">{v.planned_close ? shortDate(v.planned_close) : "—"}</td>
                                <td className="py-1 pl-2">
                                    <div className="flex flex-wrap gap-1">
                                        {(v.flags || []).map((f) => (
                                            <span key={f} className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px] text-slate-600">{FLAG_SHORT[f] || f}</span>
                                        ))}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {!rows.length && (
                            <tr><td colSpan={20} className="py-4 text-center text-slate-400">Нет вакансий по выбранным условиям</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export function FunnelWidget({ data, options }) {
    const pipe = data?.pipeline || {};
    const rows = pipe.funnel || [];
    const first = rows[0]?.reached || 0;
    const max = Math.max(1, first);
    return (
        <div className="space-y-1.5">
            <BlockError block={pipe} />
            {rows.map((r, i) => {
                const pct = options.mode === "prev" ? (i ? r.conv_prev : 100) : r.pct_of_first;
                return (
                    <div key={r.key} className="grid grid-cols-[110px_1fr_auto] items-center gap-2 text-sm">
                        <span className="text-slate-600">{r.label}</span>
                        <span className="h-5 bg-slate-100 rounded overflow-hidden">
                            <span className="block h-full bg-gradient-to-r from-[#e0bb48] to-[#cda834]" style={{ width: `${(100 * r.reached) / max}%` }} />
                        </span>
                        <span className="tabular-nums w-24 text-right"><b>{fmt(r.reached)}</b><span className="text-slate-400"> · {pct === null || pct === undefined ? "—" : `${fmt(pct, 1)}%`}</span></span>
                    </div>
                );
            })}
            {!first && <div className="text-sm text-slate-400">За период откликов нет</div>}
        </div>
    );
}

export function NowWidget({ data, options }) {
    const pipe = data?.pipeline || {};
    let rows = pipe.now || [];
    if (options.hide_empty) rows = rows.filter((r) => r.count);
    const total = rows.reduce((s, r) => s + (r.count || 0), 0);
    return (
        <div className="space-y-2">
            <BlockError block={pipe} />
            <div className="text-sm text-slate-600">Всего в работе: <b className="text-slate-900">{fmt(total)}</b></div>
            <Bars rows={rows} total={total} tone="bg-sky-400" />
        </div>
    );
}

const SERIES = { applied: ["Отклики", "#e0bb48"], hired: ["Вышли", "#10b981"], lost: ["Отсев", "#94a3b8"] };

export function FlowWidget({ data, options }) {
    const flow = data?.flow || {};
    const rows = (flow.weeks || []).map((w) => ({ ...w, label: shortDate(w.week) }));
    const series = (Array.isArray(options.series) && options.series.length ? options.series : Object.keys(SERIES)).filter((s) => SERIES[s]);
    const Chart = options.chart === "line" ? LineChart : BarChart;
    return (
        <div className="h-56">
            <BlockError block={flow} />
            <ResponsiveContainer width="100%" height="100%">
                <Chart data={rows} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {series.map((s) => (options.chart === "line"
                        ? <Line key={s} type="monotone" dataKey={s} name={SERIES[s][0]} stroke={SERIES[s][1]} strokeWidth={2} dot={false} />
                        : <Bar key={s} dataKey={s} name={SERIES[s][0]} fill={SERIES[s][1]} radius={[3, 3, 0, 0]} />))}
                </Chart>
            </ResponsiveContainer>
        </div>
    );
}

export function InterviewsWidget({ data }) {
    const iv = data?.pipeline?.interviews || {};
    const days = iv.upcoming || [];
    const max = Math.max(1, ...days.map((d) => d.count));
    return (
        <div className="space-y-2">
            <div className="grid grid-cols-7 gap-1 items-end h-24">
                {days.map((d, i) => (
                    <div key={d.date} className="flex flex-col items-center justify-end h-full">
                        <span className="text-xs tabular-nums font-semibold text-slate-700">{d.count || ""}</span>
                        <span className={`w-full rounded-t ${i === 0 ? "bg-[#e0bb48]" : "bg-sky-300"}`} style={{ height: `${Math.max(3, (70 * d.count) / max)}%` }} />
                        <span className="text-[11px] text-slate-500 mt-1">{weekday(d.date)}</span>
                        <span className="text-[10px] text-slate-400">{shortDate(d.date)}</span>
                    </div>
                ))}
            </div>
            <div className="flex gap-4 text-sm text-slate-600">
                <span>Сегодня: <b className="text-slate-900">{fmt(iv.today)}</b></span>
                <span>Неявки за период: <b className={iv.no_show ? "text-red-600" : "text-slate-900"}>{fmt(iv.no_show)}</b></span>
                <Link to="/calendar" className="ml-auto text-[#b8952b] hover:underline">Календарь →</Link>
            </div>
        </div>
    );
}

export function RequestsWidget({ data, options }) {
    const req = data?.requests || {};
    const waiting = (req.waiting || []).slice(0, Number(options.limit) || 5);
    return (
        <div className="space-y-2">
            <BlockError block={req} />
            <div className="grid grid-cols-3 gap-2">
                <Stat label="Открытых заявок" value={fmt(req.open)} />
                <Stat label="Позиций" value={fmt(req.open_positions)} />
                <Stat label="Новых за период" value={fmt(req.created_in_period)} />
            </div>
            {req.by_status?.length ? <Bars rows={req.by_status} tone="bg-violet-400" /> : null}
            {waiting.length ? (
                <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Дольше всего ждут решения</div>
                    <ul className="text-sm divide-y divide-slate-50">
                        {waiting.map((w) => (
                            <li key={w.id} className="flex justify-between gap-2 py-1">
                                <Link to="/requests" className="truncate hover:text-[#b8952b]">{w.position || `Заявка #${w.id}`} <span className="text-slate-400">{w.department || ""}</span></Link>
                                <span className={`tabular-nums whitespace-nowrap ${w.days >= 3 ? "text-red-600" : "text-slate-500"}`}>{w.status}, {w.days} дн.</span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
            {req.approved_no_vacancy ? <div className="text-sm text-amber-700">Утверждены, но вакансия не создана: <b>{req.approved_no_vacancy}</b></div> : null}
        </div>
    );
}

export function AdaptationWidget({ data, options }) {
    const ada = data?.adaptation || {};
    const riskRows = (ada.risk || []).map((r) => ({ ...r, label: RISK_LABEL[r.label] || r.label, key: r.label }));
    const total = riskRows.reduce((s, r) => s + r.count, 0);
    return (
        <div className="space-y-2">
            <BlockError block={ada} />
            <div className="grid grid-cols-3 gap-2">
                <Stat label="На адаптации" value={fmt(ada.active)} />
                <Stat label="Просрочено срезов" value={fmt(ada.overdue)} tone={ada.overdue ? "text-red-600" : undefined} />
                <Stat label="Срезов на 14 дн." value={fmt(ada.upcoming_14d)} />
            </div>
            {total ? (
                <div>
                    <div className="flex h-3 rounded overflow-hidden">
                        {riskRows.map((r) => (
                            <span key={r.key} className={RISK_TONE[r.key] || "bg-slate-300"} style={{ width: `${(100 * r.count) / total}%` }} title={`${r.label}: ${r.count}`} />
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-x-3 text-xs text-slate-500 mt-1">
                        {riskRows.map((r) => <span key={r.key}>{r.label}: <b className="text-slate-700">{r.count}</b></span>)}
                    </div>
                </div>
            ) : null}
            {options.list !== false && ada.overdue_list?.length ? (
                <ul className="text-sm divide-y divide-slate-50">
                    {ada.overdue_list.map((o) => (
                        <li key={`${o.enrollment_id}-${o.plan_date}-${o.kind}`} className="flex justify-between gap-2 py-1">
                            <Link to={`/tests/adaptation/case/${o.enrollment_id}`} className="truncate hover:text-[#b8952b]">{o.employee || "Сотрудник"} <span className="text-slate-400">{KIND_LABEL[o.kind] || o.kind}</span></Link>
                            <span className="text-red-600 tabular-nums whitespace-nowrap">−{o.days} дн.</span>
                        </li>
                    ))}
                </ul>
            ) : null}
            {ada.outcomes?.length ? (
                <div className="text-xs text-slate-500">Итоги за период: {ada.outcomes.map((o) => `${OUTCOME_LABEL[o.label] || o.label} — ${o.count}`).join(", ")}</div>
            ) : null}
        </div>
    );
}

export function StaffWidget({ data }) {
    const st = data?.staff || {};
    return (
        <div className="space-y-2">
            <BlockError block={st} />
            <div className="grid grid-cols-3 gap-2">
                <Stat label="В штате" value={fmt(st.headcount)} />
                <Stat label="Вышли за период" value={fmt(st.hired)} tone="text-emerald-700" />
                <Stat label="Уволились" value={fmt(st.left)} tone={st.left ? "text-red-600" : undefined} />
                <Stat label="Ушли до 90 дней" value={fmt(st.left_90d)} hint="ранний уход" tone={st.left_90d ? "text-red-600" : undefined} />
                <Stat label="Срок найма, дн." value={fmt(st.time_to_hire_median, 1)} hint={st.time_to_hire_n ? `медиана по ${st.time_to_hire_n}` : "нет данных"} />
                <Stat label="Чистый прирост" value={fmt((st.hired || 0) - (st.left || 0))} />
            </div>
        </div>
    );
}

export function TestsWidget({ data }) {
    const t = data?.tests || {};
    return (
        <div className="space-y-2">
            <BlockError block={t} />
            <div className="grid grid-cols-4 gap-2">
                <Stat label="Отправлено" value={fmt(t.sent)} />
                <Stat label="Результатов" value={fmt(t.results)} />
                <Stat label="Ср. балл" value={fmt(t.avg_score, 1)} />
                <Stat label="Ошибки" value={fmt(t.errors)} tone={t.errors ? "text-red-600" : undefined} />
            </div>
            {t.by_status?.length ? <Bars rows={t.by_status.map((r) => ({ ...r, label: DELIVERY_LABEL[r.label] || r.label }))} tone="bg-teal-400" /> : null}
            <Link to="/tests/results" className="text-sm text-[#b8952b] hover:underline">Результаты тестов →</Link>
        </div>
    );
}

export function LostWidget({ data, options }) {
    const pipe = data?.pipeline || {};
    const rows = (pipe.lost_reasons || []).slice(0, Number(options.limit) || 8);
    const total = pipe.lost_in_period || 0;
    return (
        <div className="space-y-2">
            <BlockError block={pipe} />
            <div className="text-sm text-slate-600">Отсеяно и отказались за период: <b className="text-slate-900">{fmt(total)}</b></div>
            {rows.length ? <Bars rows={rows} total={total} tone="bg-rose-300" /> : <div className="text-sm text-slate-400">Нет данных</div>}
        </div>
    );
}

export function RecruitersWidget({ data }) {
    const people = data?.recruiters?.people || [];
    if (!people.length) return <div className="text-sm text-slate-400">За период выходов (ВНР) нет</div>;
    return (
        <table className="w-full text-sm">
            <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                    <th className="py-1">Рекрутёр</th><th className="py-1 text-right">Выходов</th><th className="py-1 text-right">Уже ушли</th>
                </tr>
            </thead>
            <tbody>
                {people.map((p) => (
                    <tr key={p.name} className="border-b border-slate-50">
                        <td className="py-1">{p.name}</td>
                        <td className="py-1 text-right tabular-nums font-semibold">{p.hired}</td>
                        <td className={`py-1 text-right tabular-nums ${p.left ? "text-red-600" : "text-slate-400"}`}>{p.left}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

const GROUP_LABEL = { vacancies: "Вакансии", candidates: "Кандидаты", calendar: "Календарь", other: "Система" };
const PRIORITY_BORDER = { high: "bg-red-500", medium: "bg-[#e0bb48]", low: "bg-blue-400" };

export function TasksWidget({ tasks, tasksLoading, options }) {
    if (tasksLoading) return <div className="text-sm text-slate-400">Загрузка задач…</div>;
    const groups = Array.isArray(options.groups) ? options.groups : Object.keys(GROUP_LABEL);
    const list = (tasks || []).map(normalizeDashboardTask).filter((t) => groups.includes(t.group || "other"));
    if (!list.length) return <div className="text-sm text-emerald-700">Срочных задач нет.</div>;
    const byGroup = list.reduce((acc, t) => { const g = t.group || "other"; (acc[g] = acc[g] || []).push(t); return acc; }, {});
    return (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {Object.entries(byGroup).map(([g, items]) => (
                <div key={g}>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{GROUP_LABEL[g] || g} · {items.length}</div>
                    <ul className="space-y-1.5">
                        {items.map((t) => (
                            <li key={t.id} className="relative pl-3">
                                <span className={`absolute left-0 top-1 bottom-1 w-1 rounded-full ${PRIORITY_BORDER[t.priority] || "bg-slate-300"}`} />
                                <Link to={t.href || "/dashboard"} className="block text-sm font-medium text-slate-900 hover:text-[#b8952b] leading-snug">{t.title}</Link>
                                <div className="text-xs text-slate-500 leading-snug">{t.description}{t.meta ? ` · ${t.meta}` : ""}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </div>
    );
}

const STAGE_NOW = [["new", "Отклики"], ["cold", "Холодный поиск"], ["test", "Тест"], ["interview", "Собеседование"], ["test_day", "Тест-день"], ["offer", "Оффер"], ["docs", "Оформление"]];
const FUNNEL_COLS = [["test", "Тест"], ["interview", "Собес"], ["offer", "Оффер"], ["hired", "Вышли"]];
const pct = (part, whole) => (whole ? Math.round((100 * (part || 0)) / whole) : null);

export function MetricWidget({ data, options }) {
    const m = KPI_METRICS.find((x) => x.key === options.metric) || KPI_METRICS[0];
    const forbidden = (data?.meta?.forbidden || []).includes(m.block);
    const value = forbidden ? null : data?.kpi?.[m.key];
    return (
        <div className="flex flex-col justify-center h-full min-h-[64px]">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 leading-tight">{options.caption || m.label}</div>
            <div className="text-4xl font-bold tabular-nums text-slate-900">{forbidden ? "нет доступа" : fmt(value, 1)}</div>
            {data?.meta?.period_days ? <div className="text-xs text-slate-400">за {data.meta.period_days} дн.{data.meta.department ? ` · ${data.meta.department}` : ""}</div> : null}
        </div>
    );
}

export function TodayWidget({ data }) {
    const t = data?.pipeline?.today || {};
    return (
        <div className="grid grid-cols-5 gap-2">
            <Stat label="Новых откликов" value={fmt(t.applied)} />
            <Stat label="Сдвинулись" value={fmt(t.moved)} />
            <Stat label="Отсев" value={fmt(t.lost)} />
            <Stat label="Вышли" value={fmt(t.hired)} tone={t.hired ? "text-emerald-700" : undefined} />
            <Stat label="Собеседований" value={fmt(t.interviews)} />
        </div>
    );
}

export function VacancyFocusWidget({ data, options }) {
    const list = data?.pipeline?.vacancies || [];
    const v = list.find((x) => String(x.id) === String(options.vacancy));
    if (!v) return <div className="text-sm text-slate-500">Выберите вакансию в настройках модуля (⚙).{options.vacancy ? " Выбранная вакансия закрыта или в архиве." : ""}</div>;
    const rows = STAGE_NOW.map(([k, l]) => ({ label: l, count: v.stages?.[k] || 0 }));
    const f = v.funnel || {};
    return (
        <div className="space-y-2">
            <div className="flex items-start gap-2">
                <i className={`mt-1.5 w-2.5 h-2.5 rounded-full ${DOT[v.severity]}`} />
                <div className="min-w-0">
                    <Link to={`/vacancies/${v.id}/tests`} className="font-semibold text-slate-900 hover:text-[#b8952b]">{v.name}</Link>
                    <div className="text-xs text-slate-500">{v.department || "—"} · открыта {v.days_open} дн. · движение {v.last_move_days === null || v.last_move_days === undefined ? "—" : `${v.last_move_days} дн. назад`} · срок {v.planned_close ? shortDate(v.planned_close) : "не задан"}</div>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                <Stat label="В работе" value={fmt(v.active)} />
                <Stat label="Откликов за период" value={fmt(f.new)} />
                <Stat label="Вышли за период" value={fmt(v.hired_in_period)} tone={v.hired_in_period ? "text-emerald-700" : undefined} />
            </div>
            <Bars rows={rows} total={v.active} tone="bg-sky-400" />
            {f.new ? (
                <div className="text-xs text-slate-500">Из откликов за период дошли: {FUNNEL_COLS.map(([k, l]) => `${l.toLowerCase()} ${pct(f[k], f.new)}%`).join(" · ")}</div>
            ) : null}
            {v.flags?.length ? <div className="flex flex-wrap gap-1">{v.flags.map((fl) => <span key={fl} className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px] text-slate-600">{FLAG_SHORT[fl] || fl}</span>)}</div> : null}
        </div>
    );
}

export function DepartmentsWidget({ data, options }) {
    const by = {};
    (data?.pipeline?.vacancies || []).forEach((v) => {
        const k = v.department || "Без отдела";
        const d = (by[k] = by[k] || { name: k, vacancies: 0, problem: 0, active: 0, hired: 0 });
        d.vacancies += 1;
        if (v.severity !== "green") d.problem += 1;
        d.active += v.active || 0;
        d.hired += v.hired_in_period || 0;
    });
    const sorters = {
        active: (a, b) => b.active - a.active,
        vacancies: (a, b) => b.vacancies - a.vacancies,
        problem: (a, b) => b.problem - a.problem,
        name: (a, b) => a.name.localeCompare(b.name, "ru"),
    };
    const rows = Object.values(by).sort(sorters[options.sort] || sorters.active);
    if (!rows.length) return <div className="text-sm text-slate-400">Открытых вакансий нет</div>;
    return (
        <table className="w-full text-sm">
            <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                    <th className="py-1">Отдел</th><th className="py-1 text-right">Вакансий</th><th className="py-1 text-right">Проблемных</th><th className="py-1 text-right">В работе</th><th className="py-1 text-right">Вышли</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((d) => (
                    <tr key={d.name} className="border-b border-slate-50">
                        <td className="py-1">{d.name}</td>
                        <td className="py-1 text-right tabular-nums">{d.vacancies}</td>
                        <td className={`py-1 text-right tabular-nums ${d.problem ? "text-amber-700 font-semibold" : "text-slate-300"}`}>{d.problem}</td>
                        <td className="py-1 text-right tabular-nums font-semibold">{fmt(d.active)}</td>
                        <td className={`py-1 text-right tabular-nums ${d.hired ? "text-emerald-700 font-semibold" : "text-slate-300"}`}>{d.hired}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export function ConversionWidget({ data, options }) {
    const min = Number(options.min) || 0;
    let rows = (data?.pipeline?.vacancies || [])
        .filter((v) => v.funnel && (v.funnel.new || 0) >= Math.max(1, min))
        .filter((v) => !options.department || v.department === options.department)
        .map((v) => ({ ...v, conv: pct(v.funnel.interview, v.funnel.new) }));
    const sorters = {
        applied: (a, b) => b.funnel.new - a.funnel.new,
        interview: (a, b) => (b.conv ?? -1) - (a.conv ?? -1),
        worst: (a, b) => (a.conv ?? 101) - (b.conv ?? 101),
    };
    rows = rows.sort(sorters[options.sort] || sorters.applied);
    if (!rows.length) return <div className="text-sm text-slate-400">За период нет вакансий с таким числом откликов</div>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                        <th className="py-1 pr-2">Вакансия</th><th className="py-1 pr-2">Отдел</th><th className="py-1 pr-2 text-right">Откликов</th>
                        {FUNNEL_COLS.map(([k, l]) => <th key={k} className="py-1 px-2 text-right">{l}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((v) => (
                        <tr key={v.id} className="border-b border-slate-50">
                            <td className="py-1 pr-2 font-medium">{v.name}</td>
                            <td className="py-1 pr-2 text-slate-500 whitespace-nowrap">{v.department || "—"}</td>
                            <td className="py-1 pr-2 text-right tabular-nums font-semibold">{fmt(v.funnel.new)}</td>
                            {FUNNEL_COLS.map(([k]) => {
                                const p = pct(v.funnel[k], v.funnel.new);
                                return (
                                    <td key={k} className="py-1 px-2 text-right tabular-nums whitespace-nowrap">
                                        <span className="text-slate-800">{fmt(v.funnel[k])}</span>
                                        <span className="text-slate-400 text-xs"> · {p === null ? "—" : `${p}%`}</span>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function linkOptionsForRole(role) {
    const out = [];
    menuItemsForRole(role).forEach((item) => {
        if (isNavGroup(item)) item.children.forEach((c) => out.push([c.to, c.label]));
        else if (item.to !== "/dashboard") out.push([item.to, item.label]);
    });
    return out;
}

export function LinksWidget({ options, role }) {
    const allowed = new Map(linkOptionsForRole(role));
    const paths = (Array.isArray(options.paths) ? options.paths : []).filter((p) => allowed.has(p));
    if (!paths.length) return <div className="text-sm text-slate-500">Выберите разделы в настройках модуля (⚙).</div>;
    return (
        <div className="flex flex-wrap gap-2">
            {paths.map((p) => (
                <Link key={p} to={p} className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:border-[#e0bb48] hover:bg-amber-50 text-sm font-medium text-slate-700">{allowed.get(p)}</Link>
            ))}
        </div>
    );
}

export function NoteWidget({ options }) {
    if (!options.text && !options.title) return <div className="text-sm text-slate-500">Напишите текст в настройках модуля (⚙).</div>;
    return (
        <div>
            {options.title ? <div className="font-semibold text-slate-800 mb-1">{options.title}</div> : null}
            <div className="text-sm text-slate-700 whitespace-pre-wrap break-words">{options.text}</div>
        </div>
    );
}

const QUEUE_LINK = { interview_soon: "/calendar", interview_result: "/calendar" };

export function QueueWidget({ data, options }) {
    const pipe = data?.pipeline || {};
    const chosen = Array.isArray(options.items) ? options.items : null;
    let rows = (pipe.queue || []).filter((q) => !chosen || chosen.includes(q.key));
    if (options.hide_empty) rows = rows.filter((q) => q.count);
    const totalOverdue = rows.reduce((s, q) => s + (q.overdue || 0), 0);
    return (
        <div className="space-y-2">
            <BlockError block={pipe} />
            <div className="text-sm text-slate-600">
                Ждут действия: <b className="text-slate-900">{fmt(rows.reduce((s, q) => s + q.count, 0))}</b>
                {totalOverdue ? <span className="ml-3 text-red-600">просрочено: <b>{fmt(totalOverdue)}</b></span> : null}
            </div>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {rows.map((q) => (
                    <Link
                        key={q.key}
                        to={QUEUE_LINK[q.key] || "/candidates"}
                        className={`block rounded-lg border px-3 py-2 hover:shadow-sm ${q.overdue ? "border-red-200 bg-red-50/60" : q.count ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 text-slate-400"}`}
                    >
                        <div className="flex items-start justify-between gap-2">
                            <span className="text-sm leading-snug">{q.label}</span>
                            <span className="text-2xl font-bold tabular-nums leading-none">{fmt(q.count)}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 text-xs mt-0.5">
                            {q.overdue ? <span className="text-red-600">просрочено {q.overdue}{q.overdue_after_days !== null && q.overdue_after_days !== undefined ? ` (> ${q.overdue_after_days} дн.)` : ""}</span> : null}
                            {q.count && q.oldest_days ? <span className="text-slate-500">дольше всех ждёт {q.oldest_days} дн.</span> : null}
                        </div>
                        {options.vacancies !== false && q.vacancies?.length ? (
                            <div className="flex flex-wrap gap-1 mt-1">
                                {q.vacancies.map((v) => (
                                    <span key={v.id} className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px] text-slate-600 truncate max-w-[220px]" title={v.name}>{v.name} · {v.count}</span>
                                ))}
                            </div>
                        ) : null}
                    </Link>
                ))}
            </div>
        </div>
    );
}

export const WIDGET_COMPONENTS = {
    queue: QueueWidget,
    metric: MetricWidget,
    today: TodayWidget,
    vacancy_focus: VacancyFocusWidget,
    departments: DepartmentsWidget,
    conversion: ConversionWidget,
    links: LinksWidget,
    note: NoteWidget,
    alerts: AlertsWidget,
    kpi: KpiWidget,
    vacancies: VacanciesWidget,
    funnel: FunnelWidget,
    now: NowWidget,
    flow: FlowWidget,
    interviews: InterviewsWidget,
    requests: RequestsWidget,
    adaptation: AdaptationWidget,
    staff: StaffWidget,
    tests: TestsWidget,
    lost: LostWidget,
    recruiters: RecruitersWidget,
    tasks: TasksWidget,
};
